import { FormikProps } from 'formik';
import React from 'react';
import Banner from '@cpg-its/ads.containers.banner';
import { FormField } from '../../../../../global/components/form-element/form-field';
import { DividerSection } from '../../../../../global/components/layout/divider-section';
import { WysiwygWithReactLink } from '../../../../../global/components/wysiwyg-with-react-link';
import { getFormattedDate } from '../../../../../global/helpers/date-helpers';
import { PensionEnrollmentProps } from '../../../models/arc-epi-props';
import { PensionEnrollmentFormValues } from '../models/pension-enrollment-form-model';
import { PensionEnrollmentInfo } from '../models/pension-enrollment-model';
import { ContentSpacer } from '../../../../../global/components/layout/generic-content-spacer';

interface Props {
    formProps: FormikProps<PensionEnrollmentFormValues>;
    pensionInfo: PensionEnrollmentInfo;
    pensionEnrollmentProps: PensionEnrollmentProps;
}

export function PensionEnrollmentPlanSelection(props: Props) {
    const { formProps, pensionInfo, pensionEnrollmentProps } = props;

    const allowedPlans = pensionInfo?.plans?.filter(plan => (plan.eligibility_result?.errors?.length || 0) < 1);

    const EnrolledInfo = () => {
        if ((pensionInfo?.enrolled_plans?.length || 0) < 1) return null;


        return (
            <DividerSection
                sectionText={pensionEnrollmentProps?.enrolledHeader}>
                <ContentSpacer>
                    <p>{pensionEnrollmentProps?.enrolledDescription}</p>
                </ContentSpacer>
                <ul>
                    {pensionInfo?.enrolled_plans.map(plan => {
                        const dateLabel = plan.enrollment_effective_date
                            ? `${pensionEnrollmentProps?.participationDateLabel} ${getFormattedDate(plan.enrollment_effective_date)}`
                            : '';
                        return (
                            <li className='divider-section-item' key={plan.id}><strong>{plan.pbpor_description}</strong><br/>{dateLabel}</li>
                        );
                    },
                    )}
                </ul>
            </DividerSection>
        );
    };

    const NotEnrolledMessages = () => {
        // available plans that aren't already enrolled
        const notEnrolledPlans = allowedPlans?.filter(plan => !pensionInfo?.enrolled_plans?.map(enrolled => enrolled.pbpor_id).includes(plan.pbpor_id));

        const mappedMessages = notEnrolledPlans?.map(notEnrolledPlan => (pensionEnrollmentProps?.notEnrolledMessages && pensionEnrollmentProps?.notEnrolledMessages[notEnrolledPlan.pbpor_id?.toLowerCase()]) || null)?.filter(x => !!x);

        if ((mappedMessages?.length || 0) < 1) return null;

        return (
            <ContentSpacer>
                {mappedMessages.map((x, i) =>
                    <Banner
                        banner={{
                            type: 'warning',
                            label: <div className='rich-text'><WysiwygWithReactLink key={i} content={x} /></div>,
                        }} />,
                )}
            </ContentSpacer>
        );
    };

    const PlanSelection = () => {
        // filter plans that don't have eligibility errors
        const hasEnrolledPlans = (pensionInfo?.enrolled_plans?.length || 0) > 0;

        if ((allowedPlans?.length || 0) < 1) {
            return (
                <>
                    <ContentSpacer>
                        <h4>{pensionEnrollmentProps?.noPlansAvailableMessage}</h4>
                    </ContentSpacer>
                    <ContentSpacer>
                    <Banner
                        banner={{
                            type: 'warning',
                            label: <div className='rich-text'>
                                <WysiwygWithReactLink
                                    content={hasEnrolledPlans
                                        ? pensionEnrollmentProps?.allPlansEnrolledContent
                                        : pensionEnrollmentProps?.noPlansAvailableContent} />
                                </div>,
                        }}
                    />
                    </ContentSpacer>
                </>
            );
        }

        const planOptions = allowedPlans.map(plan => {
            return {
                label: plan.pbpor_description,
                value: plan.pbpor_id,
            };
        });


        return (
            <DividerSection
                sectionText={pensionEnrollmentProps?.planSelectionHeader}
                removeDivider>
                <NotEnrolledMessages />

                <h3 className='divider-section-item-header'>{pensionEnrollmentProps?.eligiblePlansLabel}</h3>
                <p>{pensionEnrollmentProps?.eligiblePlansDescription}</p>

                <FormField
                    name='plan'
                    component='radioGroup'
                    options={planOptions}
                    touched={formProps.touched}
                    errors={formProps.errors}
                    required
                />
            </DividerSection>
        );
    };

    return (
        <>
            <EnrolledInfo />   
            <PlanSelection />
        </>
    );
}